import "./companionMenu.css";

export type CompanionAction = "resume" | "navigate" | "inventory" | "journal" | "character" | "signals" | "restart" | "race-menu";
export type CompanionChoice = readonly [CompanionAction, string, string, string];
const defaultChoices: readonly CompanionChoice[] = [
  ["resume", "Continue", "The world is waiting for us.", "M7 4l8 8-8 8"],
  ["navigate", "Atlas", "A whole sky of possibilities.", "M12 3l3 6 6 3-6 3-3 6-3-6-6-3 6-3z"],
  ["inventory", "Inventory", "Everything we have picked up along the way.", "M5 8h14v12H5z M9 8V4h6v4"],
  ["journal", "Journal", "A few stories still unfolding.", "M5 4h14v16H5z M9 8h6 M9 12h6 M9 16h4"],
  ["character", "Character", "Let’s see how far you have come.", "M8 7a4 4 0 1 0 8 0a4 4 0 1 0-8 0 M4 21v-2a8 8 0 0 1 16 0v2"],
  ["signals", "Nearby signals", "I can point out what is around us.", "M12 12h.01 M8 8a6 6 0 0 0 0 8 M16 8a6 6 0 0 1 0 8 M5 5a10 10 0 0 0 0 14 M19 5a10 10 0 0 1 0 14"],
];

/** DOM owns selection and focus; the companion and player remain in the real scene. */
export class CompanionMenu {
  readonly el = document.createElement("section");
  private readonly buttons: HTMLButtonElement[];
  private readonly message: HTMLElement;
  private previousFocus: HTMLElement | null = null;
  private selected = 0;
  visible = false;

  constructor(private readonly handlers: { onAction: (action: CompanionAction) => void; onReact: () => void; onPointer: (x: number, y: number) => void }, private readonly choices: readonly CompanionChoice[] = defaultChoices) {
    this.el.id = "companion-menu";
    this.el.setAttribute("role", "dialog"); this.el.setAttribute("aria-modal", "true");
    this.el.setAttribute("aria-labelledby", "companion-title");
    this.el.hidden = true; this.el.inert = true;
    this.el.innerHTML = `
      <div class="cm-wash" aria-hidden="true"></div>
      <header class="cm-masthead"><span>SOUL <b>MERIDIAN</b></span><span class="cm-connection"><i></i> COMPANION LINK</span></header>
      <div class="cm-panel">
        <p class="cm-eyebrow">A MOMENT BETWEEN WORLDS</p>
        <h1 id="companion-title">Where to next?</h1>
        <p class="cm-intro">A little light for the journey.</p>
        <nav aria-label="Companion menu">${this.choices.map(([id, label, , path], i) => `
          <button type="button" data-action="${id}" style="--row:${i}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="${path}"/></svg><span>${label}</span><span class="cm-arrow" aria-hidden="true">↗</span></button>`).join("")}</nav>
        <p class="cm-message" aria-live="polite">The world is waiting for us.</p>
      </div>
      <footer class="cm-footer"><span class="cm-presence"><i></i> RIGHT HERE WITH YOU</span><span><kbd>↑</kbd><kbd>↓</kbd> Choose <kbd>↵</kbd> Select <button type="button" class="cm-close" aria-label="Close companion menu"><kbd>TAB</kbd> Return</button></span></footer>`;
    document.body.append(this.el);
    this.buttons = Array.from(this.el.querySelectorAll<HTMLButtonElement>("nav button"));
    this.message = this.el.querySelector<HTMLElement>(".cm-message")!;
    this.buttons.forEach((button, index) => {
      button.addEventListener("pointerenter", () => this.select(index, false));
      button.addEventListener("focus", () => this.select(index, false));
      button.addEventListener("click", () => handlers.onAction(this.choices[index][0]));
    });
    this.el.querySelector(".cm-close")!.addEventListener("click", () => handlers.onAction("resume"));
    this.el.addEventListener("pointermove", e => handlers.onPointer(e.clientX / innerWidth * 2 - 1, 1 - e.clientY / innerHeight * 2));
  }

  show(signals = false): void {
    this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.visible = true; this.el.hidden = false; this.el.inert = false;
    // Establish the closed style after display:none so the first opening fades too.
    void this.el.offsetWidth;
    this.el.classList.add("open"); document.body.classList.add("companion-open");
    const signalButton = this.buttons[this.choices.findIndex(([id]) => id === "signals")];
    if (signalButton) {
      signalButton.setAttribute("aria-pressed", String(signals));
      signalButton.querySelector("span")!.textContent = signals ? "Hide nearby signals" : "Nearby signals";
    }
    this.select(0, true);
  }

  hide(): void {
    this.visible = false; this.el.inert = true; this.el.classList.remove("open");
    if (this.el.contains(document.activeElement)) (document.activeElement as HTMLElement)?.blur();
    this.previousFocus?.focus({ preventScroll: true });
    this.previousFocus = null;
  }

  finishClose(): void { if (!this.visible) { this.el.hidden = true; document.body.classList.remove("companion-open"); } }

  /** Called by the host's capture handler so no gameplay hotkey leaks through. */
  key(e: KeyboardEvent): void {
    e.preventDefault(); e.stopImmediatePropagation();
    if (e.repeat && !["ArrowUp", "ArrowDown"].includes(e.code)) return;
    if (e.code === "Tab" || e.code === "Escape") this.handlers.onAction("resume");
    else if (e.code === "ArrowDown" || e.code === "ArrowUp") this.select((this.selected + (e.code === "ArrowDown" ? 1 : -1) + this.buttons.length) % this.buttons.length, true);
    else if (e.code === "Home" || e.code === "End") this.select(e.code === "Home" ? 0 : this.buttons.length - 1, true);
    else if (e.code === "Enter" || e.code === "Space") this.handlers.onAction(this.choices[this.selected][0]);
  }

  private select(index: number, focus: boolean): void {
    if (this.selected !== index) this.handlers.onReact();
    this.selected = index;
    this.buttons.forEach((b, i) => { b.classList.toggle("selected", i === index); b.tabIndex = i === index ? 0 : -1; });
    this.message.textContent = this.choices[index][2];
    if (focus) this.buttons[index].focus({ preventScroll: true });
  }
}
